// WP-3.1 — Operational SEO configuration (DARK / fail-closed).
//
// Single source for the governed VITE_* operational-SEO flags. Everything is
// OFF / empty by default: with no env set, verification emits nothing, the
// consent banner stays hidden, and analytics cannot load. Activation is a
// deploy-time concern only (production is frozen — Implementation Governance Mode).
//
// Architecture: these are OPERATIONAL CONSUMERS (ADR-0009 §4.5 / §7) — they own
// no entity identity and no content; they never write to the registry. The split
// below keeps the decision logic PURE (unit-testable with explicit args) while a
// thin set of live readers pulls the actual build env via import.meta.env.

// Analytics providers wired today. OPS-4 (ratified 2026-06-27): GTM is the SINGLE
// governed tag container — the app loads only GTM, which fronts GA4 + Microsoft
// Clarity inside the container (no direct vendor tags in app code). Still
// consent-gated + DARK (no container id → nothing loads).
export const KNOWN_ANALYTICS_PROVIDERS = Object.freeze(['gtm']);
export const ANALYTICS_PROVIDER_KEY = 'gtm';

export function normalizeToken(v) {
  return typeof v === 'string' ? v.trim() : '';
}

// Analytics is "configured" only when a KNOWN provider AND a non-empty id are set.
// With KNOWN_ANALYTICS_PROVIDERS empty this is always false → analytics stays dark.
export function isAnalyticsConfigured({ provider, id } = {}) {
  const p = normalizeToken(provider);
  const i = normalizeToken(id);
  return p !== '' && i !== '' && KNOWN_ANALYTICS_PROVIDERS.includes(p);
}

// --- live build-env readers (thin; pulled from import.meta.env) ---
const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};

export const GSC_VERIFICATION = normalizeToken(env.VITE_GSC_VERIFICATION);
export const BING_VERIFICATION = normalizeToken(env.VITE_BING_VERIFICATION);

// Master switch for the consent banner UI. Default OFF (dark) per the WP-3.1
// owner decision "build dark, enable later". Analytics is gated regardless.
export const CONSENT_ENABLED = env.VITE_CONSENT_ENABLED === 'true';

export const ANALYTICS_PROVIDER = normalizeToken(env.VITE_ANALYTICS_PROVIDER);
export const ANALYTICS_ID = normalizeToken(env.VITE_ANALYTICS_ID);

// OPS-4 — the GTM container id is the canonical analytics config. DARK when empty.
export const GTM_CONTAINER_ID = normalizeToken(env.VITE_GTM_CONTAINER_ID);

export function analyticsConfiguredFromEnv() {
  return isAnalyticsConfigured({ provider: ANALYTICS_PROVIDER_KEY, id: GTM_CONTAINER_ID });
}
